<template>
  <div class="ys-splash">
    <div class="ys-splash-inner">
      <img class="ys-splash-logo" :src="logoImg" alt="카이온" />
      <p class="ys-splash-loading" aria-live="polite">로딩 중입니다.</p>
      <span class="ys-splash-spinner" aria-hidden="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { authStore } from '@/stores/auth'
import { pendingNotifyTarget, consumePendingNotify } from '@/composables/useNotifyDeepLink'
import logoImg from '@/assets/images/brand/caion-logo-clear.png'

const router = useRouter()

const BOOT_TIMEOUT_MS = 5000
const MIN_SPLASH_MS = 800
const NAV_TIMEOUT_MS = 2000
const FAILSAFE_MS = 3500
const ESCAPE_KEY = 'caion_splash_escape'

let failsafeTimer: ReturnType<typeof setTimeout> | null = null
let navigated = false

/** 현재 URL query를 유지한 entry 경로 (문자 ?source=sms 등) */
const entryWithQuery = () => {
  const q = router.currentRoute.value.query
  const params = new URLSearchParams()
  Object.keys(q).forEach((key) => {
    const v = q[key]
    if (v == null) return
    if (Array.isArray(v)) v.forEach((x) => x != null && params.append(key, String(x)))
    else params.set(key, String(v))
  })
  const qs = params.toString()
  return qs ? `/entry?${qs}` : '/entry'
}

const resolveTarget = () => {
  const user = authStore.state.user
  if (!user) return entryWithQuery()
  // 푸시 알림으로 연 딥링크 우선 (채팅창·일정 등)
  const pending = pendingNotifyTarget()
  if (pending) return pending
  const home = authStore.homeForRole(user.appRole)
  return home && home !== '/splash' ? home : entryWithQuery()
}

const hardEscape = (target: string) => {
  const n = Number(sessionStorage.getItem(ESCAPE_KEY) || '0')
  if (n >= 2) {
    sessionStorage.removeItem(ESCAPE_KEY)
    window.location.replace(entryWithQuery())
    return
  }
  sessionStorage.setItem(ESCAPE_KEY, String(n + 1))
  window.location.replace(target === '/splash' ? entryWithQuery() : target)
}

const goNext = async (target: string) => {
  if (navigated) return
  navigated = true
  authStore.state.booting = false

  try {
    await Promise.race([
      router.replace(target).catch(() => undefined),
      new Promise<void>((r) => setTimeout(r, NAV_TIMEOUT_MS)),
    ])
    // 딥링크로 도착했으면 pending 소모 (셸 flush 와 중복 이동 방지)
    const pending = pendingNotifyTarget()
    if (pending && (router.currentRoute.value.fullPath === pending || router.currentRoute.value.path === pending.split('?')[0])) {
      consumePendingNotify()
    }
  } catch {
    /* ignore */
  }

  // 가드 루프·네비게이션 hang 시 스플래시에 남음 → 하드 탈출 (루프 방지 카운터)
  if (router.currentRoute.value.path === '/splash') {
    hardEscape(target)
    return
  }
  sessionStorage.removeItem(ESCAPE_KEY)
}

const runBoot = async () => {
  const started = Date.now()
  try {
    await Promise.race([
      authStore.bootstrap(),
      new Promise<void>((r) => setTimeout(r, BOOT_TIMEOUT_MS)),
    ])
  } catch {
    /* ignore */
  } finally {
    authStore.state.booting = false
  }

  if (!authStore.state.user) {
    try {
      await Promise.race([
        authStore.bootstrap(),
        new Promise<void>((r) => setTimeout(r, 1500)),
      ])
    } catch {
      /* ignore */
    } finally {
      authStore.state.booting = false
    }
  }

  const wait = Math.max(0, MIN_SPLASH_MS - (Date.now() - started))
  if (wait) await new Promise((r) => setTimeout(r, wait))

  await goNext(resolveTarget())
}

const onPageShow = (e: PageTransitionEvent) => {
  // bfcache 복원 시 onMounted가 다시 안 돌아 스플래시에 고정되는 경우
  if (e.persisted && router.currentRoute.value.path === '/splash') {
    navigated = false
    void goNext(resolveTarget())
  }
}

onMounted(() => {
  window.addEventListener('pageshow', onPageShow)

  // 어떤 이유로 boot Promise가 멈춰도 강제 탈출
  failsafeTimer = setTimeout(() => {
    if (router.currentRoute.value.path === '/splash') {
      void goNext(resolveTarget())
    }
  }, FAILSAFE_MS)

  void runBoot().finally(() => {
    if (failsafeTimer) {
      clearTimeout(failsafeTimer)
      failsafeTimer = null
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('pageshow', onPageShow)
  if (failsafeTimer) {
    clearTimeout(failsafeTimer)
    failsafeTimer = null
  }
})
</script>
